import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui'

/**
 * Error boundary for the CarlaChatPane message-rendering surface
 * (Sprint Z Chunk 7).
 *
 * Phase A audit (2026-04-26) flagged that a Markdown crash on a
 * malformed assistant reply unmounts the entire pane — the user
 * sees a blank screen with no recovery affordance. This boundary
 * catches React render-time errors in the message list and shows
 * a fallback with a "Reload" button.
 *
 * Module-scoped: only CarlaChatPane uses it today. Foundation Docs
 * mention a general `<ApiErrorBoundary>` (referenced in
 * `lib/api/jsonFetch.ts:13`) but it doesn't exist yet. If a second
 * consumer appears, hoist this to `@/components/ui/` per the
 * ≥2-consumers rule.
 *
 * Note: only catches **render-time** errors. Async errors during
 * SSE streaming or fetch are still handled by the existing catch
 * blocks in `useCarlaChat.ts`. Error boundaries don't catch:
 * - Async errors (event handlers, promises, setTimeout)
 * - Errors during server-side rendering
 * - Errors in the boundary itself
 */

export interface ChatErrorBoundaryProps {
  children: ReactNode
  /** Called when the user clicks the Reload button. Caller decides
   *  whether to reset selectedId, refetch the conversation, or
   *  reload the page. */
  onReload?: () => void
}

interface State {
  error: Error | null
}

export class ChatErrorBoundary extends Component<ChatErrorBoundaryProps, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Telemetry hook — surfaces in browser console for now.
    // A future Sentry/equivalent integration would route through
    // here.
     
    console.error(
      '[CARLA] Render error caught by ChatErrorBoundary:',
      error,
      info.componentStack,
    )
  }

  handleReset = (): void => {
    this.setState({ error: null })
    this.props.onReload?.()
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <AlertTriangle size={28} className="text-warning-text opacity-70" />
        <div className="flex flex-col gap-1">
          <p className="m-0 text-sm font-medium text-secondary">
            Something went wrong rendering this conversation.
          </p>
          <p className="m-0 text-xs text-tertiary">
            Your messages are safe — they're stored on the server. Reload to
            try again.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={this.handleReset}>
          <RotateCcw size={14} />
          Reload conversation
        </Button>
        <details className="mt-2 max-w-md text-left text-2xs text-tertiary">
          <summary className="cursor-pointer">Error details</summary>
          <pre className="mt-1 overflow-auto rounded-xs border border-border-subtle bg-surface-sunk p-2 text-2xs">
            {error.message}
          </pre>
        </details>
      </div>
    )
  }
}
